import { Link } from 'react-router-dom';
import { PASSWORD_RESET_NOTICE } from '@racksavant/ui';
import { LegalDoc, type LegalSection } from '../components/LegalDoc';
import s from '../components/LegalDoc.module.css';

const LAST_UPDATED = '2 August 2026';

const SECTIONS: LegalSection[] = [
  {
    id: 'short',
    title: 'The short version',
    summary: 'Private by default. No training on your photos. Delete means delete. No measurements, ever.',
    body: (
      <>
        <p>
          RackSavant is a try-on studio. To work, it needs at least one photograph of you and a
          record of what you have tried on. That is genuinely sensitive material, so the four
          commitments below sit above everything else in this document.
        </p>
        <ul>
          <li>
            <strong>Private by default.</strong> Your photos and the looks made from them are
            visible only to you until you post them, share a link, or bring them into a fitting
            room.
          </li>
          <li>
            <strong>No model training.</strong> We do not train models on your photographs, and we
            do not licence them to anyone else for training.
          </li>
          <li>
            <strong>Deletion cascades.</strong> Delete a photo and every look generated from it is
            deleted with it, in the same operation.
          </li>
          <li>
            <strong>No body measurements.</strong> We do not ask for them, store them, estimate them
            or infer them. There is no field for it.
          </li>
        </ul>
        <p>
          The rest of this document explains the detail. It is long because being specific is the
          only way to be honest, not because there is anything buried in it.
        </p>
      </>
    ),
  },
  {
    id: 'who',
    title: 'Who we are',
    summary: 'RackSavant Labs, Inc. is the controller of your data. Email us and a person replies.',
    body: (
      <>
        <p>
          RackSavant is operated by RackSavant Labs, Inc. For the purposes of the UK and EU General
          Data Protection Regulation, we are the data controller for the personal data described
          here.
        </p>
        <p>
          Privacy questions, access requests and deletion requests all go to{' '}
          <a href="mailto:privacy@racksavant.com">privacy@racksavant.com</a>. That inbox is read by
          a person, not a ticket robot, and we aim to reply within five working days.
        </p>
      </>
    ),
  },
  {
    id: 'collect',
    title: 'What we collect',
    summary: 'Account details, the photos you upload, the looks you generate, and basic technical logs.',
    body: (
      <>
        <p>Everything we hold falls into one of these categories.</p>
        <ul>
          <li>
            <strong>Account data.</strong> Your email address, username, display name, and a hashed
            password — or, if you sign in with Google, the identifier and email Google returns to
            us. We never receive or store your Google password.
          </li>
          <li>
            <strong>Body shots.</strong> The photographs of yourself that you upload in order to try
            things on. See the next section.
          </li>
          <li>
            <strong>Looks and mood reads.</strong> The images the service generates for you, the
            garments used, the moods you chose, and the written read attached to each look.
          </li>
          <li>
            <strong>Content you create.</strong> Lookbooks, captions, room chat messages, hype
            reactions, follows, and — on Studio — garments and drops you upload and publish.
          </li>
          <li>
            <strong>Usage data.</strong> Which features you use and when, so we can tell whether the
            product works. Aggregated wherever it can be.
          </li>
          <li>
            <strong>Technical data.</strong> IP address, browser and device type, and error logs.
            These are kept short-term for security, abuse prevention and debugging.
          </li>
          <li>
            <strong>Payment data.</strong> If you subscribe, our payment processor handles your card
            details. We receive the subscription status, the last four digits and the billing
            country. We never see or store a full card number.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'bodyshot',
    title: 'What a body shot is',
    summary: 'A photo of you, stored privately, used only as the base image for your own fittings.',
    body: (
      <>
        <p>
          Because it is the most sensitive thing here, it deserves its own definition rather than a
          line in a list.
        </p>
        <dl className={s.define}>
          <dt>Body shot</dt>
          <dd>
            A photograph of yourself that you upload so the service can render clothing onto it.
            Typically full-length, taken by you or someone you asked. It is stored in private object
            storage and served only over short-lived signed URLs issued to your own session.
          </dd>
          <dt>What it is used for</dt>
          <dd>
            One thing: generating your looks. It is sent to our image-generation subprocessor
            together with the garment images and the instruction to change only the clothing. It is
            not used for advertising, not used for training, not used to build a profile of you, and
            not shown to other users unless you publish a look made from it.
          </dd>
          <dt>What it is not</dt>
          <dd>
            It is not a biometric identifier. We do not compute a face template from it, we do not
            run face recognition or matching, and we do not derive measurements, weight, body
            composition, age or health information from it.
          </dd>
        </dl>
        <p>
          You may store several body shots and mark one as primary. You may delete any of them at
          any time — see <a href="#retention">deletion and retention</a>.
        </p>
      </>
    ),
  },
  {
    id: 'never',
    title: 'What we never collect',
    summary: 'No measurements, no biometric templates, no face matching, no ad tracking.',
    body: (
      <>
        <p>
          This list is a commitment, not a description of a current gap in our roadmap. If any of it
          ever changes, we will say so prominently and in advance, not in a version diff.
        </p>
        <ul>
          <li>Body measurements, clothing sizes, weight, or any estimate of body dimensions.</li>
          <li>
            Biometric identifiers or templates. We do not create faceprints and we do not use your
            photos to identify you or anyone else.
          </li>
          <li>Health, medical or fitness data.</li>
          <li>
            Precise location. We infer an approximate country from your IP address for tax and legal
            purposes; we do not collect GPS location.
          </li>
          <li>
            Third-party advertising identifiers. There are no ad-network trackers or advertising
            pixels on RackSavant.
          </li>
          <li>Your contacts, camera roll, or anything else on your device that you did not choose to upload.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'use',
    title: 'How we use it',
    summary: 'To run the service you asked for, keep it safe, bill you, and fix what is broken.',
    body: (
      <>
        <p>We use personal data for these purposes and no others:</p>
        <ul>
          <li>
            <strong>Providing the service.</strong> Generating looks, writing mood reads, running
            fitting rooms, storing your lookbooks. Legal basis: performance of our contract with
            you.
          </li>
          <li>
            <strong>Keeping accounts secure.</strong> Authentication, session management, abuse and
            fraud prevention, rate limiting. Legal basis: legitimate interests.
          </li>
          <li>
            <strong>Support.</strong> Answering you when you write to us. Legal basis: performance
            of contract and legitimate interests.
          </li>
          <li>
            <strong>Billing.</strong> Taking payment and meeting tax obligations. Legal basis:
            contract and legal obligation.
          </li>
          <li>
            <strong>Improving the product.</strong> Aggregated usage patterns — which features are
            used, where people get stuck. Legal basis: legitimate interests. This never involves
            looking at your photographs.
          </li>
          <li>
            <strong>Legal compliance.</strong> Responding to lawful requests and enforcing our{' '}
            <Link to="/terms">terms</Link>. Legal basis: legal obligation and legitimate interests.
          </li>
        </ul>
        <p>
          We do not sell personal data, and we do not share it for cross-context behavioural
          advertising.
        </p>
      </>
    ),
  },
  {
    id: 'ai',
    title: 'AI processing and model training',
    summary: 'Your photos are sent to Google for generation, then deleted from there. Nobody trains on them.',
    body: (
      <>
        <p>
          Generating a look requires sending your body shot and the selected garment images to a
          third-party image model. We use Google’s Gemini models for both image generation and the
          written mood read. That transfer is the entire point of the product and cannot be
          switched off while still using it.
        </p>
        <p>
          We use Google’s paid API tier, under terms which state that content submitted through it
          is not used to train Google’s models. We do not train any model on your content either.
          Our prompts carry two hard instructions on every request: preserve the person’s face,
          body, pose, skin and age unchanged, and describe only the clothing.
        </p>
        <p>
          Generated output is stored in our own storage under your account. We do not run automated
          decision-making that produces legal or similarly significant effects about you.
        </p>
      </>
    ),
  },
  {
    id: 'subprocessors',
    title: 'Who else touches your data',
    summary: 'Cloudflare hosts and stores. Google generates. A payment processor bills. That is the list.',
    body: (
      <>
        <p>We keep the number of companies involved deliberately small.</p>
        <ul>
          <li>
            <strong>Cloudflare, Inc.</strong> — hosting, edge compute, database and object storage.
            Your account data, body shots and looks are stored on Cloudflare infrastructure.
          </li>
          <li>
            <strong>Google LLC (Gemini API)</strong> — image generation and the written mood read.
            Receives body shots and garment images at the moment of a fitting.
          </li>
          <li>
            <strong>Our payment processor</strong> — subscription billing. Receives your email and
            payment details directly; we receive only the subscription state.
          </li>
        </ul>
        <p>
          There is deliberately no transactional email provider on that list yet:{' '}
          <strong>{PASSWORD_RESET_NOTICE.title}</strong>. Account recovery is manual —{' '}
          {PASSWORD_RESET_NOTICE.body}. Until that changes, no third party receives your email
          address on our behalf for account mail, because no account mail is sent.
        </p>
        <p>
          Each processor above is bound by a data processing agreement limiting them to processing
          on our instructions. We will publish an updated list here before adding anyone new,
          including whoever eventually sends the reset emails.
        </p>
      </>
    ),
  },
  {
    id: 'visibility',
    title: 'Who can see your looks',
    summary: 'Only you, until you decide otherwise. Rooms, links and the Runway are all opt-in.',
    body: (
      <>
        <p>Every look starts private. Visibility changes only when you change it:</p>
        <ul>
          <li>
            <strong>Private.</strong> Visible to you alone. This is the default for everything.
          </li>
          <li>
            <strong>Shared by link.</strong> Anyone holding the link can view that one look. Links
            can be revoked.
          </li>
          <li>
            <strong>In a fitting room.</strong> Visible to the people in that room while it is open.
            Rooms expire twelve hours after they are created.
          </li>
          <li>
            <strong>Public.</strong> Posted to the Runway feed and your profile, where anyone can
            see it and react to it. You can make a public look private again at any time.
          </li>
        </ul>
        <p>
          Body shots themselves are never public. Only the generated look is ever shareable, and
          only when you share it.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'Deletion and retention',
    summary: 'Delete a photo and its looks go too. Delete the account and everything goes within 30 days.',
    body: (
      <>
        <p>
          <strong>Deleting a body shot</strong> removes the image and every look generated from it,
          together with the mood reads attached to those looks. This is a cascade, not a flag: the
          objects are removed from storage. Anything you have already exported to your own device or
          posted elsewhere is obviously outside our reach.
        </p>
        <p>
          <strong>Deleting your account</strong> removes your profile, body shots, looks, lookbooks,
          uploaded garments, drops and room history. We complete this within 30 days. Encrypted
          backups roll off on a 30-day cycle, so a deleted object can persist in a backup for up to
          that long before it is overwritten; it is not restored to the live service in the
          meantime.
        </p>
        <p>Otherwise:</p>
        <ul>
          <li>Fitting rooms and their chat are destroyed twelve hours after creation.</li>
          <li>Technical and security logs are kept for 30 days.</li>
          <li>
            Billing records are kept for as long as tax law requires, typically seven years. These
            contain transaction records, not photographs.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'rights',
    title: 'Your rights in the EU and UK',
    summary: 'Access, correct, delete, port, object, complain. Free, and within a month.',
    body: (
      <>
        <p>
          If you are in the European Economic Area, the United Kingdom or Switzerland, you have the
          right to: access the personal data we hold about you; correct it if it is wrong; have it
          erased; restrict or object to processing; receive it in a portable format; and withdraw
          consent where processing is based on consent.
        </p>
        <p>
          Exercise any of these by emailing{' '}
          <a href="mailto:privacy@racksavant.com">privacy@racksavant.com</a>. We will respond within
          one month and there is no charge. Account and look deletion can also be done immediately
          in the app, without asking us.
        </p>
        <p>
          You also have the right to complain to your local supervisory authority — in the UK, the
          Information Commissioner’s Office. We would rather you told us first, but it is your
          right either way.
        </p>
      </>
    ),
  },
  {
    id: 'california',
    title: 'Your rights in California',
    summary: 'Know, delete, correct, opt out. We do not sell or share your data, so there is nothing to opt out of.',
    body: (
      <>
        <p>
          Under the California Consumer Privacy Act as amended, California residents have the right
          to know what personal information we collect and why, to request deletion or correction,
          to limit the use of sensitive personal information, and to opt out of sale or sharing.
        </p>
        <p>
          We do not sell personal information and we do not share it for cross-context behavioural
          advertising, so there is no opt-out to operate. Photographs are treated as sensitive
          personal information and are used only to provide the service you asked for.
        </p>
        <p>
          We will not discriminate against you for exercising any of these rights. Requests go to{' '}
          <a href="mailto:privacy@racksavant.com">privacy@racksavant.com</a>; you may use an
          authorised agent, and we may need to verify your identity before acting.
        </p>
      </>
    ),
  },
  {
    id: 'transfers',
    title: 'International transfers',
    summary: 'Data may be processed in the US under standard contractual clauses.',
    body: (
      <>
        <p>
          We are a United States company and our subprocessors operate globally, so your data may be
          processed outside your own country, including in the US.
        </p>
        <p>
          Where data leaves the EEA or UK, transfers are covered by the European Commission’s
          Standard Contractual Clauses and the UK International Data Transfer Addendum, along with
          the additional safeguards our processors have in place. Cloudflare storage is configured
          to keep object data in-region where that option is available.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    title: 'Age requirements',
    summary: '13+ generally, 16+ in the EEA and UK. Under-18s should not post publicly.',
    body: (
      <>
        <p>
          RackSavant is not for children. You must be at least 13 years old to create an account,
          and at least 16 if you are in the European Economic Area or the United Kingdom, where that
          is the age at which a person can consent to this kind of processing without a parent.
        </p>
        <p>
          If you are under 18, please keep your looks private rather than posting them to the public
          Runway feed. Accounts we identify as belonging to someone under the minimum age are
          deleted, along with all their content. If you believe a child has created an account,
          write to <a href="mailto:privacy@racksavant.com">privacy@racksavant.com</a> and we will
          act quickly.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Security',
    summary: 'Encrypted in transit and at rest, private media behind signed URLs, hashed passwords.',
    body: (
      <>
        <p>
          All traffic is served over TLS. Stored objects are encrypted at rest. Private media —
          including every body shot — is not publicly addressable and is served only through
          short-lived signed URLs bound to your session. Passwords are hashed with PBKDF2-SHA256 at
          210,000 iterations; we cannot read them.
        </p>
        <p>
          Access to production systems is limited to the small number of people who need it, and no
          one browses user photographs. If a breach affects your personal data, we will notify you
          and the relevant regulator within the timeframes the law requires.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and analytics',
    summary: 'Session cookies to keep you signed in. No advertising trackers.',
    body: (
      <>
        <p>
          We use a small number of strictly necessary cookies and local storage entries: your
          session token, your refresh token, and interface preferences such as whether you last
          viewed prices monthly or yearly. These are required for the service to function and cannot
          be turned off while you are signed in.
        </p>
        <p>
          We use privacy-preserving, aggregate product analytics to count feature usage. There are
          no third-party advertising cookies, no cross-site tracking pixels, and no data brokers.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    summary: 'Material changes get notice in advance, not a silent edit.',
    body: (
      <>
        <p>
          We will update this document as the product changes. The date at the top always reflects
          the current version.
        </p>
        <p>
          If a change materially affects how we handle your photographs — in particular anything
          touching the four commitments in section one — we will tell you by email and in the app at
          least 30 days before it takes effect, so you can export your content and close your
          account if you disagree.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    summary: 'privacy@racksavant.com. A person reads it.',
    body: (
      <>
        <p>
          Privacy questions, data requests and complaints:{' '}
          <a href="mailto:privacy@racksavant.com">privacy@racksavant.com</a>.
        </p>
        <p>
          Anything else: <a href="mailto:hello@racksavant.com">hello@racksavant.com</a>. Postal
          address available on request while our registered office details are being finalised.
        </p>
      </>
    ),
  },
];

export function PrivacyPage() {
  return (
    <LegalDoc
      eyebrow="Legal"
      title="Privacy policy"
      subtitle="What we collect, why, who else sees it, and how to make it all go away. Written to be read, not to be survived."
      lastUpdated={LAST_UPDATED}
      contactEmail="privacy@racksavant.com"
      sections={SECTIONS}
      siblingLabel="Read the terms of service"
      siblingTo="/terms"
    />
  );
}
